import React from 'react';
import { motion } from 'framer-motion';
import { Ruler, FileSignature, CreditCard, CloudRain, Languages, Calculator } from 'lucide-react';

const features = [
  {
    icon: Ruler,
    title: 'Photo Measure (LiDAR)',
    description: 'A phone photo becomes a precise square-foot count in 30 seconds — irregular shapes, cutouts and all.',
  },
  {
    icon: FileSignature,
    title: 'One-Tap Contracts',
    description: 'Your template, your terms, your payment milestones. In the customer’s inbox seconds after the call.',
  },
  {
    icon: CreditCard,
    title: 'Customer Portal + Stripe',
    description: 'Sarah signs with her finger and pays the deposit by card — a clean web page, no app to install.',
  },
  {
    icon: CloudRain,
    title: 'Weather-Aware Scheduling',
    description: 'Real forecasts for every job site. Rain Thursday? One tap reschedules and notifies the customer.',
  },
  {
    icon: Calculator,
    title: 'Concrete Calculators',
    description: 'Yardage from area and thickness, rebar runs, and more — the math contractors actually need, built in.',
  },
  {
    icon: Languages,
    title: 'Bilingual From Day One',
    description: 'Toggle the entire app to Spanish for the crew doing the work — not bolted on, designed in.',
  },
];

const stats = [
  { value: '30s', label: 'From phone photo to square-foot count' },
  { value: '1 tap', label: 'From signed estimate to deposit invoice' },
  { value: '2 langs', label: 'Full English / Spanish toggle' },
];

const phoneShots = [
  { src: '/wrc/dashboard.png', caption: 'Pipeline & active jobs at a glance' },
  { src: '/wrc/pricing.png', caption: 'Areas auto-price, line items, live total' },
  { src: '/wrc/portal.png', caption: 'Customer portal — sign & pay' },
];

const WRCShowcase: React.FC = () => {
  return (
    <motion.section
      className="bg-gray-900/80 border border-gold-800/30 rounded-3xl overflow-hidden shadow-2xl shadow-gold-900/30 backdrop-blur-sm mb-12"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.8 }}
    >
      {/* Hero image: LiDAR photo measure */}
      <div className="relative h-72 md:h-96 bg-black overflow-hidden">
        <motion.img
          src="/wrc/photo-measure.jpg"
          alt="WRC Concrete Pro — LiDAR photo measurement over a driveway"
          className="w-full h-full object-cover object-center"
          initial={{ scale: 1.15 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 2.2, ease: 'easeOut' }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/40 to-transparent" />
        <div className="absolute top-4 left-4 flex items-center gap-2">
          <span className="bg-gold-400 text-black text-xs font-bold px-3 py-1 rounded-full tracking-wide">CLIENT BUILD</span>
          <span className="bg-black/60 border border-gold-700/40 text-gold-200 text-xs font-semibold px-3 py-1 rounded-full">Live</span>
        </div>
        <div className="absolute bottom-5 left-6 right-6">
          <p className="text-gold-300 text-xs font-semibold tracking-[0.3em] uppercase mb-1">WRC · Water · Rock · Cement</p>
          <h2 className="text-3xl md:text-4xl font-bold text-gold-400">Concrete Pro</h2>
          <p className="text-gray-200 text-sm md:text-base">The residential concrete contractor OS</p>
        </div>
      </div>

      <div className="p-6 md:p-10">
        <p className="text-gray-300 text-base md:text-lg max-w-3xl mb-8">
          Quote a driveway, sign the contract, take the deposit, schedule the pour, and weather-check the
          week — without ever opening a spreadsheet or driving over to the customer with a clipboard.
          Built for concrete, not bolted on: one customer call, from first ring to first deposit.
        </p>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          {stats.map((stat) => (
            <div key={stat.value} className="bg-gold-900/20 border border-gold-800/40 rounded-2xl p-5 text-center">
              <div className="text-3xl font-bold text-gold-400 mb-1">{stat.value}</div>
              <p className="text-gray-300 text-sm">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Feature grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
          {features.map((feature) => {
            const Icon = feature.icon;
            return (
              <div
                key={feature.title}
                className="bg-gold-900/20 border border-gold-800/40 rounded-2xl p-5 hover:border-gold-600/50 transition-colors duration-300"
              >
                <div className="w-11 h-11 rounded-xl bg-gold-400/15 border border-gold-700/30 flex items-center justify-center mb-3 text-gold-400">
                  <Icon className="w-6 h-6" strokeWidth={1.75} />
                </div>
                <h3 className="text-gold-400 font-semibold mb-1">{feature.title}</h3>
                <p className="text-gray-300 text-sm leading-relaxed">{feature.description}</p>
              </div>
            );
          })}
        </div>

        {/* Phone screenshots */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
          {phoneShots.map((shot, i) => (
            <motion.figure
              key={shot.src}
              className="flex flex-col items-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.6, delay: i * 0.12 }}
            >
              <div className="rounded-[2rem] border-4 border-gray-800 bg-black shadow-xl shadow-gold-900/20 overflow-hidden">
                <img src={shot.src} alt={shot.caption} className="w-full h-auto block" loading="lazy" />
              </div>
              <figcaption className="text-gray-400 text-xs text-center mt-3 px-2">{shot.caption}</figcaption>
            </motion.figure>
          ))}
        </div>

        {/* CTA */}
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <motion.a
            href="https://www.wrcnow.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-6 py-3 bg-gold-400 text-black font-semibold rounded-xl shadow-lg shadow-gold-500/30 transition-all duration-300 hover:bg-gold-300 pulse-glow"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Visit WRC Now
          </motion.a>
          <span className="text-gray-400 text-sm">See it on your own jobs at wrcnow.com</span>
        </div>
      </div>
    </motion.section>
  );
};

export default WRCShowcase;
